import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import "media-chrome";
import tippy from "tippy.js";
import { ScrollMenu } from "./scrollMenu.js";
import { HorizontalScroller } from "./horizontalScroller.js";
import { Shoji } from "./shoji.js";

gsap.registerPlugin(ScrollTrigger);

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

const mm = gsap.matchMedia();

gsap.defaults({
  duration: 0.25,
  ease: "power1.inOut",
});

const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

if (
  typeof CSSLayerBlockRule !== "function" &&
  !localStorage.getItem("oldBrowserBanner")
) {
  // Add a banner to the page
  const banner = document.createElement("a");
  banner.style =
    "position:fixed;top:0;left:0;width:100%;padding:1rem;background:red;text-align:center;color:#fff;z-index:9999999;cursor:pointer";

  // Check language
  const lang = navigator.language || navigator.userLanguage;
  if (lang.startsWith("de")) {
    banner.innerHTML =
      "Sie verwenden einen veralteten Browser. Die Website kann nicht korrekt angezeigt werden.";
  } else {
    banner.innerHTML =
      "You are using an outdated browser. The website can not be displayed correctly.";
  }

  banner.addEventListener("click", (e) => {
    banner.remove();
    localStorage.setItem("oldBrowserBanner", "dismissed");
  });

  // Add the banner to the body
  document.body.appendChild(banner);
}

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

// Create the guides
const guides = document.createElement("div");
guides.className = "guides wrap";
const guidesInner = document.createElement("div");
guidesInner.className = "inner grid";
guides.appendChild(guidesInner);
for (let i = 0; i < 12; i++) {
  const guide = document.createElement("div");
  guide.className = "guides__guide";
  guidesInner.appendChild(guide);
}
document.body.appendChild(guides);

// Set up a tween that animates the guides in and out
let toggleGuides = gsap
  .to(guides, {
    duration: 0.25,
    ease: "power2.inOut",
    autoAlpha: 1,
    paused: true,
  })
  .reverse();

// Check if the guides was enabled before
if (localStorage.getItem("debug") == "true") {
  toggleGuides.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener("keydown", (e) => {
  if (e.key === "g" && e.ctrlKey) {
    // Toggle the guides when ⌃G is pressed
    toggleGuides.reversed() ? toggleGuides.play() : toggleGuides.reverse();
    // Save the state in local storage
    localStorage.setItem("debug", !toggleGuides.reversed());
  }
});

guides.dataset.windowWidth = window.innerWidth;

window.addEventListener("resize", () => {
  guides.dataset.windowWidth = window.innerWidth;
});

/* -------------------------------------------------- */
/* ↔️ Horizontal Scrollers */
/* -------------------------------------------------- */

document.addEventListener("DOMContentLoaded", () => {
  new HorizontalScroller({
    elements: document.querySelectorAll(".horizontal-scroller"),
    autoHeight: true,
  });
});

/* -------------------------------------------------- */
/* 💆‍♂️ Header */
/* -------------------------------------------------- */

mm.add("(min-width: 1000px)", () => {
  const headerTl = gsap.timeline({
    defaults: {
      ease: "power1.out",
    },
    scrollTrigger: {
      trigger: ".header",
      start: () => {
        if (document.querySelector(".banner")) {
          return 45;
        }
        return 0;
      },
      end: "+=150",
      scrub: 0.25,
      invalidateOnRefresh: true,
    },
  });

  let logoSVG = document.querySelector(".header .logo__svg, .header .logo__img");
  let signet = document.querySelector(".header .logo .signet");

  if (signet) {
    headerTl
      .to(
        ".menu",
        {
          y: "1.1rem",
        },
        0
      )
      .to(
        ".logo",
        {
          y: "0.5rem",
        },
        0
      )
      .to(
        ".header",
        {
          y: "-5rem",
          boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.15)",
        },
        0
      )
      .to(
        ".header__contact",
        {
          autoAlpha: 0,
          y: "1rem",
        },
        0
      )
      .to(
        ".header .logo .signet",
        {
          autoAlpha: 0,
        },
        0
      );
  } else {
    let logoSize = getComputedStyle(logoSVG).getPropertyValue("width");
    let smallLogoSize = parseFloat(logoSize) / 1.5;

    headerTl
      .to(
        ".header",
        {
          boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.15)",
        },
        0
      )
      .to(
        ".header__inner",
        {
          paddingTop: "0.75rem",
          paddingBottom: "0.75rem",
        },
        0
      )
      .to(
        ".header .menus",
        {
          rowGap: "0.75rem",
        },
        0
      )
      .to(
        ".header .logo__svg",
        {
          width: smallLogoSize,
        },
        0
      );
  }

  return () => {
    headerTl.kill();
    gsap.set(
      ".menu, .header, .header__contact, .header .logo, .header .logo .signet",
      {
        clearProps: "all",
      }
    );
  };
});

/* -------------------------------------------------- */
/* 📜 Scroll menu */
/* -------------------------------------------------- */

let scrollMenu = document.querySelector("[data-scroll-menu]");

if (scrollMenu) {
  document.addEventListener("DOMContentLoaded", () => {
    new ScrollMenu(scrollMenu);
  });
}

/* -------------------------------------------------- */
/* 🌅 Hero Images */
/* -------------------------------------------------- */

// Group faders by their data-fader-group attribute
const faderGroups = {};
document.querySelectorAll("[data-fader-group]").forEach((item) => {
  const groupName = item.dataset.faderGroup;
  if (!faderGroups[groupName]) {
    faderGroups[groupName] = [];
  }
  faderGroups[groupName].push(item);
});

// Process each group
Object.entries(faderGroups).forEach(([groupName, faderItems]) => {
  const faders = faderItems.map((item) => Array.from(item.children));
  let currentIndices = faders.map(() => 0);

  function fadeNext(faderIndex) {
    const currentFader = faders[faderIndex];
    const currentImageIndex = currentIndices[faderIndex];
    const nextImageIndex = (currentImageIndex + 1) % currentFader.length;

    gsap.to(currentFader[currentImageIndex], {
      autoAlpha: 0,
      duration: 2,
      ease: "power3.inOut",
    });

    gsap.to(currentFader[nextImageIndex], {
      autoAlpha: 1,
      duration: 2,
      ease: "power3.inOut",
    });

    currentIndices[faderIndex] = nextImageIndex;
  }

  const interval = 4;

  function fadeLoop(index = 0) {
    fadeNext(index);

    const nextIndex = (index + 1) % faderItems.length;

    gsap.delayedCall(interval, () => {
      fadeLoop(nextIndex);
    });
  }

  gsap.delayedCall(interval, () => {
    fadeLoop();
  });
});

/* -------------------------------------------------- */
/* ✨ Overlays */
/* -------------------------------------------------- */

const modalHandler = new Shoji({
  urlAttribute: "data-overlay",
  onContentLoaded: (modal, link) => {
    // Pass the color variables to the modal
    if (link.hasAttribute("data-colorful")) {
      const linkStyle = getComputedStyle(link);
      modal.style.setProperty(
        "--color-text",
        linkStyle.getPropertyValue("--color-text")
      );
      modal.style.setProperty(
        "--color-background",
        linkStyle.getPropertyValue("--color-background")
      );
    }

    // Initialize horizontal scrollers in the modal
    new HorizontalScroller({
      elements: modal.querySelectorAll(".horizontal-scroller"),
      autoHeight: true,
    });
  },
});

modalHandler.init();

/* -------------------------------------------------- */
/* 🔤 Logo links */
/* -------------------------------------------------- */

const logoLinksContainers = document.querySelectorAll(".logo-links");

logoLinksContainers.forEach((container) => {
  const viewport = container.querySelector(".logo-links__viewport");
  const itemsWrapper = container.querySelector(".logo-links__items");

  if (isReducedMotion()) {
    gsap.set(viewport, {
      overflowX: "auto",
      paddingInline: "var(--spacing-inline)",
    });
  } else {
    if (itemsWrapper.scrollWidth > container.clientWidth) {
      const clones = [];
      for (let i = 0; i < 10; i++) {
        const clone = itemsWrapper.cloneNode(true);
        clone.setAttribute("aria-hidden", true);

        let cloneLinks = clone.querySelectorAll("a");
        cloneLinks.forEach((link) => {
          link.setAttribute("tabindex", -1);
        });

        viewport.appendChild(clone);
        clones.push(clone);
      }

      // Set up the animation
      const totalWidth = itemsWrapper.scrollWidth;
      const duration = totalWidth / 50;

      let animation = gsap.to([itemsWrapper, ...clones], {
        xPercent: -100,
        duration: duration,
        repeat: -1,
        ease: "none",
        modifiers: {
          xPercent: gsap.utils.wrap(-600, 0),
        },
      });

      // Start the animation from the middle
      animation.progress(0.5);

      container.addEventListener("mouseenter", function () {
        gsap.to(animation, {
          timeScale: 0,
          duration: 0.5,
          ease: "power1.inOut",
        });
      });

      container.addEventListener("mouseleave", function () {
        gsap.to(animation, {
          timeScale: 1,
          duration: 1,
          ease: "power1.inOut",
        });
      });
    }
  }
});

/* -------------------------------------------------- */
/* 🪗 Accordions */
/* -------------------------------------------------- */

const accordionItems = document.querySelectorAll(".menu-accordion-item");

const openAccordionItem = (item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  button.setAttribute("aria-expanded", "true");
  content.setAttribute("aria-hidden", "false");

  gsap.to(content, {
    height: "auto",
    duration: isReducedMotion() ? 0 : 0.5,
    ease: "power1.inOut",
    onComplete: () => gsap.set(content, { height: "auto" }),
  });
  gsap.to(icon, { rotation: 45 });

  item.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

const closeAccordionItem = (item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  button.setAttribute("aria-expanded", "false");
  content.setAttribute("aria-hidden", "true");

  gsap.to(content, {
    height: 0,
    duration: isReducedMotion() ? 0 : 0.5,
    ease: "power1.inOut",
  });
  gsap.to(icon, { rotation: 0 });
};

const updateUrl = (id = null) => {
  if (id) {
    history.pushState(null, null, `#${id}`);
  } else {
    const canonicalUrl = document.querySelector("link[rel='canonical']")?.href;
    history.pushState(null, null, canonicalUrl || window.location.pathname);
  }
};

accordionItems.forEach((item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  gsap.set(content, { height: 0, overflow: "hidden", display: "block" });
  gsap.set(icon, { rotation: 0 });

  button.addEventListener("click", () => {
    const isExpanded = button.getAttribute("aria-expanded") === "true";

    if (!isExpanded) {
      updateUrl(content.id);
      openAccordionItem(item);
    } else {
      updateUrl();
      closeAccordionItem(item);
    }
  });
});

window.addEventListener("load", () => {
  const hash = window.location.hash.slice(1);
  if (hash) {
    const targetItem = document.getElementById(hash);
    if (targetItem) {
      const targetAccordion = targetItem.closest(".menu-accordion-item");
      if (targetAccordion) {
        openAccordionItem(targetAccordion);
      }
    }
  } else {
    accordionItems.forEach(closeAccordionItem);
  }
});

/* -------------------------------------------------- */
/* 📱 Mobile Menu */
/* -------------------------------------------------- */

let menuToggle = document.querySelector(".menu-toggle");
let menuToggleOpen = document.querySelector(".menu-toggle__text--open");
let menuToggleClose = document.querySelector(".menu-toggle__text--close");
let menu = document.querySelector(".menu");

mm.add("(max-width: 1000px)", () => {
  const menuAnimation = gsap
    .timeline({
      paused: true,
      defaults: {
        duration: 0.5,
        ease: "power2.inOut",
      },
    })

    .fromTo(
      menu,
      {
        autoAlpha: 1,
        yPercent: -100,
      },
      {
        autoAlpha: 1,
        yPercent: 0,
      }
    )

    .to(
      menuToggleOpen,
      {
        autoAlpha: 0,
      },
      "<"
    )

    .to(
      menuToggleClose,
      {
        autoAlpha: 1,
      },
      "<"
    );

  let clickHandler = (e) => {
    menuToggle.setAttribute(
      "aria-expanded",
      menuToggle.getAttribute("aria-expanded") === "true" ? "false" : "true"
    );
    if (menuToggle.getAttribute("aria-expanded") === "true") {
      menuAnimation.play();
    } else {
      menuAnimation.reverse();
    }
  };

  menuToggle.addEventListener("click", clickHandler);

  return () => {
    menuAnimation.kill();
    menuToggle.removeEventListener("click", clickHandler);
    gsap.set([menu, menuToggleOpen, menuToggleClose], {
      clearProps: "all",
    });
  };
});

/* -------------------------------------------------- */
/* 🎈 Popups */
/* -------------------------------------------------- */

const popup = document.querySelector(".popup");

if (popup) {
  const closeButton = popup.querySelector(".popup__close-button");

  // Let's generate an ID for the popup so if it changes, it will show again
  const popupId = popup.textContent.replace(/\s/g, "").slice(0, 50);
  const localStoragePopupState = localStorage.getItem("popup-" + popupId);

  if (localStoragePopupState !== "closed") {
    popup.showModal();
  }

  const closePopup = () => {
    popup.close();
    localStorage.setItem("popup-" + popupId, "closed");
  };

  // Close on click on the close button
  closeButton.addEventListener("click", () => {
    closePopup();
  });

  // Close on click outside/backdrop
  popup.addEventListener("click", (e) => {
    if (e.target === popup) {
      closePopup();
    }
  });
}

/* -------------------------------------------------- */
/* 🤮 DIRS21 */
/* -------------------------------------------------- */

const dirs21Button = document.querySelector(".dirs21");

if (dirs21Button) {
  loadDIRS21();

  dirs21Button.addEventListener("click", (e) => {
    d21_api.ibe.open();
    e.preventDefault();
  });
}

function loadDIRS21() {
  loadScript("https://code.jquery.com/jquery-3.6.0.min.js");
  loadScript(dirs21Button.href);
}

function loadScript(src) {
  const script = document.createElement("script");
  script.src = src;
  document.body.appendChild(script);
}

/* -------------------------------------------------- */
/* 🗾 Map */
/* -------------------------------------------------- */

let maps = document.querySelectorAll(".map");

// Global promise to track Mapbox GL JS loading
let mapboxGLPromise = null;

const loadMapboxGL = () => {
  if (mapboxGLPromise) return mapboxGLPromise;

  mapboxGLPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
    script.onload = () => {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
      } else {
        reject(new Error("Mapbox GL JS failed to load"));
      }
    };
    script.onerror = () =>
      reject(new Error("Failed to load Mapbox GL JS script"));
    document.head.appendChild(script);
  });

  return mapboxGLPromise;
};

// Load Mapbox GL JS on page load
if(maps.length) {
  loadMapboxGL();
}

const initMap = async (mapEl) => {
  try {
    const mapboxgl = await loadMapboxGL();

    let mapData = JSON.parse(mapEl.dataset.map);

    mapboxgl.accessToken = mapData.accessToken;

    let map = new mapboxgl.Map({
      container: mapEl,
      style: mapData.styleUrl,
      center: [mapData.center.lon, mapData.center.lat],
      zoom: mapData.zoom,
      pitch: 30,
    });

    let bounds = new mapboxgl.LngLatBounds();

    let places = mapData.collection;

    places.features.forEach(function (marker) {
      var el = document.createElement("div");
      el.id = marker.properties.title;
      el.className = "poi " + marker.properties.class;
      el.style = marker.properties.style;
      el.setAttribute("data-lat", marker.properties.lat);
      el.setAttribute("data-lon", marker.properties.lon);
      new mapboxgl.Marker(el, {
        anchor: "bottom",
      })
        .setLngLat(marker.geometry.coordinates)
        .addTo(map);
      bounds.extend(marker.geometry.coordinates);
    });

    map.scrollZoom.disable();
    map.dragRotate.disable();

    map.on("load", () => {
      map.resize();
    });

    return map;
  } catch (error) {
    console.error("Error initializing map:", error);
    mapEl.textContent = "Failed to load map";
  }
};

maps.forEach((mapEl) => {
  let module = mapEl.closest(".module");
  let map;

  // Setup intersection observer to init map when it enters viewport
  let observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initMap(mapEl)
            .then((initializedMap) => {
              if (initializedMap) {
                map = initializedMap;
                setupMapInteractions(module, map);
              }
            })
            .catch((error) => {
              console.error("Error in map initialization:", error);
            });
          observer.unobserve(mapEl);
        }
      });
    },
    {
      rootMargin: "100px",
      threshold: 0.1,
    }
  );

  observer.observe(mapEl);
});

function setupMapInteractions(module, map) {
  // POI clicks
  module.addEventListener("click", function (e) {
    if (e.target.matches(".poi, .poi *")) {
      let poi = e.target.closest(".poi") || e.target;
      const id = poi.id;

      module
        .querySelectorAll(".poi")
        .forEach((el) => el.classList.remove("active"));
      module
        .querySelectorAll(".place-headlines__item")
        .forEach((el) => (el.ariaCurrent = "false"));

      poi.classList.add("active");
      module.querySelector(
        `.place-headlines__item[data-id="${id}"]`
      ).ariaCurrent = "true";

      map.flyTo({
        center: [poi.dataset.lon, poi.dataset.lat],
        zoom: 17,
        speed: 0.5,
      });
    }
  });

  // Fly buttons
  const flyButtons = module.querySelectorAll(".fly-buttons__item");
  flyButtons.forEach((flyButton) => {
    flyButton.addEventListener("click", function () {
      if (flyButtons.length > 1) {
        flyButtons.forEach((el) => {
          el.ariaCurrent = el === flyButton ? "false" : "true";
        });
      }

      map.flyTo({
        center: [this.dataset.lon, this.dataset.lat],
        zoom: Number(this.dataset.zoom),
        speed: 0.5,
      });
    });
  });

  // Zoom in handler
  module
    .querySelector(".zoom-buttons__item--in")
    .addEventListener("click", () => {
      map.zoomIn({ duration: 1000 });
    });

  // Zoom out handler
  module
    .querySelector(".zoom-buttons__item--out")
    .addEventListener("click", () => {
      map.zoomOut({ duration: 1000 });
    });
}

/* -------------------------------------------------- */
/* 📌 Tooltips */
/* -------------------------------------------------- */

let tooltips = document.querySelectorAll("[data-tooltip]");

tooltips.forEach((tooltip) => {
  tippy(tooltip, {
    content: tooltip.dataset.tooltip,
    theme: "custom",
  });
});
